import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { useServiceToken, useSnackbar } from '@ltvco/refresh-lib/v1';
import { useQuery } from '@ltvco/refresh-lib/vendors';
import { useState, useEffect, useRef, useContext } from 'react';

import {
  makeAuthenticatedGetRequest,
  resetSearchRefAfterDelay,
  createDelay,
  createSearchFunction,
  useCourtCasesApi,
  API_CONFIG,
  DEFAULT_QUERY_OPTIONS,
  ERROR_MESSAGES,
} from '../api';
import { type JudgeSearchResponse, type JudgeResult } from '../types';
import {
  updateUrlParams,
  normalizeAdvancedFilters,
  getOrderByParam,
  prepareQueryForCacheKey,
  processExactMatchQuery,
} from '../utils';

// ===================== Core API Hook =====================
/**
 * Core hook that provides authenticated API methods for judge search
 * @returns API methods and authentication state
 */
export const useJudgeSearchApi = () => {
  const {
    data: tokenData,
    refetch: refetchToken,
    isLoading: isTokenLoading,
  } = useServiceToken('oram');

  // Handles API calls with token refresh logic
  const makeApiCall = <T>(
    apiCall: (token: string) => Promise<T>,
    retryCount = 0
  ): Promise<T> => {
    if (!tokenData?.token) {
      throw new Error(ERROR_MESSAGES.TOKEN_UNAVAILABLE);
    }
    return apiCall(tokenData.token).catch(async (error: unknown) => {
      if (
        error instanceof Error &&
        error.message?.includes('401') &&
        retryCount === 0
      ) {
        try {
          await refetchToken();
          await createDelay(100);
          return makeApiCall(apiCall, retryCount + 1);
        } catch {
          throw new Error(ERROR_MESSAGES.TOKEN_REFRESH_FAILED);
        }
      }
      throw error;
    });
  };

  // Get shared methods from main court cases API
  const { fetchPaginationPage } = useCourtCasesApi();

  // API Methods
  const searchJudges = createSearchFunction<JudgeSearchResponse>(
    'p',
    makeApiCall
  );

  /**
   * Fetches judge details by ID from person endpoint
   * @param id - person ID
   * @returns Judge details or null if not found
   */
  const getJudge = async (id: string): Promise<JudgeResult | null> => {
    return makeApiCall(async (token: string) => {
      const url = `${API_CONFIG.BASE_URL}/legal/people/?id=${id}`;
      const data = await makeAuthenticatedGetRequest<JudgeSearchResponse>(
        url,
        token
      );
      return data.results?.[0] || null;
    });
  };

  return {
    searchJudges,
    fetchPaginationPage,
    getJudge,
    isLoading: isTokenLoading,
    tokenData,
  };
};

// ===================== Main Search Hook =====================
/**
 * Main hook for judge search functionality
 * @param initialQuery - Initial search query string
 * @param exactMatch - Whether to use exact match search mode
 * @returns Search state and functions for judge searches
 */
export const useJudgeSearch = (
  initialQuery: string = '',
  exactMatch: boolean = false
) => {
  // State
  const [sortOption, setSortOption] = useState('score desc');
  const [currentQuery, setCurrentQuery] = useState('');
  const [currentFilters, setCurrentFilters] = useState<
    Record<string, string | number | boolean>
  >({});
  const [judgeSearchResults, setJudgeSearchResults] =
    useState<JudgeSearchResponse | null>(null);
  const [searchExecuted, setSearchExecuted] = useState(false);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [currentExactMatch, setCurrentExactMatch] = useState(exactMatch);

  // Refs
  const isSearchingRef = useRef(false);
  const isUpdatingStateRef = useRef(false);

  // Contexts
  const { enqueueSnackbar } = useSnackbar();
  const { logError } = useContext(AppConfig);
  const {
    searchJudges,
    isLoading: isTokenLoading,
    tokenData,
  } = useJudgeSearchApi();

  // Computed values
  const hasAnyFilters = Object.keys(currentFilters || {}).length > 0;
  const hasValidSearchParams = Boolean(
    Boolean(currentQuery && currentQuery.trim().length > 0) || hasAnyFilters
  );
  const shouldExecuteQuery = Boolean(
    searchExecuted && hasValidSearchParams && !isUpdatingStateRef.current
  );

  // Error Handler
  const handleSearchError = (error: unknown, context: string) => {
    if (
      error instanceof Error &&
      !error.message?.includes('Search query is required')
    ) {
      logError(`Judge search error in ${context}`, error);
      enqueueSnackbar(ERROR_MESSAGES.GENERAL_ERROR, { variant: 'error' });
    }
  };

  const executeSearch = async (): Promise<JudgeSearchResponse> => {
    const orderBy = getOrderByParam(sortOption, false, true);

    // Process query based on exact match setting
    const processedQuery = currentQuery.trim()
      ? processExactMatchQuery(currentQuery, currentExactMatch)
      : currentQuery;

    try {
      const results = await searchJudges(
        processedQuery,
        orderBy,
        currentFilters
      );
      return results;
    } catch (error) {
      logError('Failed to search judges', error as Error);
      enqueueSnackbar('Failed to search judges. Please try again.', {
        variant: 'error',
      });
      throw error;
    }
  };

  const filtersKey =
    Object.keys(currentFilters).length > 0
      ? JSON.stringify(currentFilters)
      : 'no-filters';

  // Success handler for search query
  const handleSearchSuccess = (data: JudgeSearchResponse) => {
    setJudgeSearchResults(data);
    updateUrlParams({ query: currentQuery.trim() });
    setSearchExecuted(false);
  };

  // Error handler for search query
  const handleSearchQueryError = (error: unknown) => {
    handleSearchError(error, 'search query execution');
    setSearchExecuted(false);
    setJudgeSearchResults(null);
  };

  // Create the query key with exactMatch to ensure cache invalidation when it changes
  const queryKeyQuery = prepareQueryForCacheKey(
    currentQuery,
    currentExactMatch
  );

  const queryKey = ['judge-search', queryKeyQuery, sortOption, filtersKey];

  // Main Search Query
  const searchQuery = useQuery(queryKey, executeSearch, {
    enabled: shouldExecuteQuery && !isTokenLoading && !!tokenData?.token,
    staleTime: API_CONFIG.STALE_TIME,
    cacheTime: API_CONFIG.CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: (failureCount: number, error: unknown) => {
      if (
        error instanceof Error &&
        error.message?.includes('Search query is required')
      ) {
        return false;
      }
      return failureCount < API_CONFIG.MAX_RETRIES;
    },
    onSuccess: handleSearchSuccess,
    onError: handleSearchQueryError,
  });

  // Update exact match state when prop changes and force a new search if needed
  useEffect(() => {
    if (currentExactMatch !== exactMatch) {
      setCurrentExactMatch(exactMatch);

      // If there's an active search query, force a refetch with the new exactMatch setting
      if (currentQuery.trim() && searchQuery.data) {
        searchQuery.refetch();
      }
    }
  }, [exactMatch, currentQuery, currentExactMatch, searchQuery]);

  // Effects
  useEffect(() => {
    if (initialQuery && initialQuery.trim()) {
      isUpdatingStateRef.current = true;
      setCurrentQuery(initialQuery);
      setTimeout(() => {
        isUpdatingStateRef.current = false;
        setSearchExecuted(true);
      }, 0);
    }
  }, [initialQuery]);

  useEffect(() => {
    if (searchQuery.data && !searchQuery.isLoading) {
      setJudgeSearchResults(searchQuery.data);
    }
  }, [searchQuery.data, searchQuery.isLoading]);

  // Actions
  const triggerSearch = (query: string) => {
    if (query.trim()) {
      isSearchingRef.current = true;
      isUpdatingStateRef.current = true;
      setCurrentQuery(query);
      setTimeout(() => {
        isUpdatingStateRef.current = false;
        setSearchExecuted(true);
      }, 0);
      resetSearchRefAfterDelay(isSearchingRef);
    }
  };

  const updateSortOption = (newSortOption: string) => {
    setSortOption(newSortOption);
  };

  const updateAdvancedFilters = (filters: Record<string, unknown> = {}) => {
    const apiReadyFilters = normalizeAdvancedFilters(filters, false, true);
    setCurrentFilters(
      apiReadyFilters as Record<string, string | number | boolean>
    );
  };

  const triggerCombinedSearch = (
    query: string,
    filters: Record<string, unknown> = {}
  ) => {
    isSearchingRef.current = true;
    const apiReadyFilters = normalizeAdvancedFilters(filters, false, true);
    isUpdatingStateRef.current = true;
    setCurrentFilters(
      apiReadyFilters as Record<string, string | number | boolean>
    );

    if (
      apiReadyFilters.order_by &&
      typeof apiReadyFilters.order_by === 'string' &&
      apiReadyFilters.order_by !== sortOption
    ) {
      setSortOption(apiReadyFilters.order_by);
    }

    setCurrentQuery(query);
    setTimeout(() => {
      isUpdatingStateRef.current = false;
      setSearchExecuted(true);
    }, 0);
    resetSearchRefAfterDelay(isSearchingRef);
  };

  const updateJudgeSearchResults = (newResults: JudgeSearchResponse) => {
    setJudgeSearchResults(newResults);
    setIsPaginationLoading(false);
  };

  const setPaginationLoading = (loading: boolean) => {
    setIsPaginationLoading(loading);
  };

  // Computed values
  const isSearching = searchExecuted && searchQuery.isLoading;
  const isAnyLoading = isSearching || isPaginationLoading;
  const hasResults = !!judgeSearchResults;
  const hasError = !!searchQuery.error;
  const generalError =
    searchQuery.error &&
    !(
      searchQuery.error instanceof Error &&
      searchQuery.error.message?.includes('Search query is required')
    )
      ? ERROR_MESSAGES.GENERAL_ERROR
      : null;

  return {
    // Data
    judgeSearchResults,
    searchQuery: currentQuery,
    sortOption,

    // States
    isSearching,
    isAnyLoading,
    hasResults,
    hasError,
    generalError,
    isTokenLoading,
    isPaginationLoading,

    // Actions
    triggerSearch,
    triggerCombinedSearch,
    setSortOption: updateSortOption,
    updateAdvancedFilters,
    updateJudgeSearchResults,
    setPaginationLoading,
    setSearchExecuted,

    // React-query helpers
    error: searchQuery.error,
    data: searchQuery.data,
    refetch: searchQuery.refetch,
  };
};

/**
 * Custom hook for fetching judge details
 * @param id - Judge ID
 * @returns Judge details query results
 */
export const useJudgeDetails = (id: string | undefined) => {
  const { getJudge, isLoading: isTokenLoading } = useJudgeSearchApi();

  // Query for judge information
  const judgeQuery = useQuery(['judge', id], () => (id ? getJudge(id) : null), {
    ...DEFAULT_QUERY_OPTIONS,
    enabled: !!id && !isTokenLoading,
  });

  return {
    judgeQuery,
    isLoading: judgeQuery.isLoading || isTokenLoading,
    isError: judgeQuery.isError,
    error: judgeQuery.error,
    data: judgeQuery.data,
  };
};
